import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom-v6'
import { toast } from 'sonner'
import { ApiError } from '@clouway/api'
import { Empty, EmptyHeader, EmptyTitle, Skeleton } from '@clouway/ui'

import { useConfigTemplates, type ConfigTemplate } from '@/features/configTemplates'
import { TemplateApplyCard } from '../components/TemplateApplyCard'
import { useTrackingObject, useUpdateTrackingObjectConfig } from '../hooks'
import type { TrackingObjectConfig } from '../types'

// Apply pushes only the template's port/sensor configs — never its commands
// (which aren't part of the device config document anyway).
export function applyTemplateConfig(template: ConfigTemplate): TrackingObjectConfig {
  return {
    id: '',
    analogPortConfigs: template.analogPortConfigs,
    digitalPortConfigs: template.digitalPortConfigs,
    sensorConfiguration: template.sensorConfiguration,
  }
}

export function TrackingObjectTemplateConfig() {
  const { t } = useTranslation('feature')
  const { id = '', tid = '' } = useParams<{ id: string; tid: string }>()
  const navigate = useNavigate()
  const { data: object } = useTrackingObject(id, tid)
  const { data: templates, isLoading } = useConfigTemplates()
  const { mutateAsync: applyConfig } = useUpdateTrackingObjectConfig(id, tid)

  // Only templates built for this object's device type are applicable.
  const usable = templates?.filter((template) => template.device.type === object?.type) ?? []

  const handleApply = async (template: ConfigTemplate) => {
    try {
      await applyConfig(applyTemplateConfig(template))
      toast.success(t('trackingObjects.config.successTemplateConfig'))
      navigate(`/customers/${id}/tracking-objects`)
    } catch (error) {
      const code = error instanceof ApiError ? error.status : ''
      toast.error(t('trackingObjects.config.errorTemplateConfig', { code }))
    }
  }

  if (isLoading) return <Skeleton className="mx-auto h-40 w-full max-w-2xl" />

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
      {usable.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('configTemplates.list.emptyMessage')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      ) : (
        usable.map((template) => (
          <TemplateApplyCard key={template.id} template={template} onConfirm={handleApply} />
        ))
      )}
    </div>
  )
}
